import { ref, onValue, off, get } from 'firebase/database';
import { database } from './firebase';
import { GalleryImage } from '../types/gallery';

class GalleryService {
  private useFirebase = !!database;

  private getGalleryRef() {
    if (!database) {
      return null;
    }
    return ref(database, 'gallery');
  }

  /**
   * Subscribe to gallery images - tries Firebase first, falls back to local JSON
   * @param callback Function to call when data changes
   * @returns Unsubscribe function
   */
  subscribeToGalleryImages(callback: (images: GalleryImage[]) => void): () => void {
    console.log('[GalleryService] Setting up subscription to gallery');

    if (this.useFirebase) {
      try {
        const galleryRef = this.getGalleryRef();
        if (!galleryRef) {
          this.useFirebase = false;
          this.loadLocalGallery().then(callback);
          return () => {};
        }

        const handleData = (snapshot: any) => {
          const data = snapshot.val();
          console.log('[GalleryService] Received gallery data from Firebase:', data);

          if (!data) {
            console.log('[GalleryService] No gallery data in Firebase, trying local fallback');
            this.loadLocalGallery().then(callback);
            return;
          }

          const images = this.parseFirebaseData(data);
          callback(images);
        };

        const handleError = (error: any) => {
          console.error('[GalleryService] Firebase error, falling back to local:', error);
          this.useFirebase = false;
          this.loadLocalGallery().then(callback);
        };

        // Subscribe to real-time updates
        onValue(galleryRef, handleData, handleError);

        // Return unsubscribe function
        return () => {
          console.log('[GalleryService] Unsubscribing from Firebase gallery');
          off(galleryRef);
        };
      } catch (error) {
        console.error('[GalleryService] Firebase setup error, using local:', error);
        this.useFirebase = false;
        this.loadLocalGallery().then(callback);
        return () => {};
      }
    } else {
      this.loadLocalGallery().then(callback);
      return () => {};
    }
  }

  /**
   * Parse Firebase data into GalleryImage array
   */
  private parseFirebaseData(data: any): GalleryImage[] {
    const images: GalleryImage[] = Object.entries(data).map(([id, imageData]: [string, any]) => ({
      id,
      imageUrl: imageData.imageUrl,
      thumbnailUrl: imageData.thumbnailUrl || imageData.imageUrl,
      title: imageData.title || '',
      description: imageData.description || '',
      uploadedAt: imageData.uploadedAt || Date.now(),
      uploadedBy: imageData.uploadedBy,
      order: imageData.order ?? 999,
      visible: imageData.visible !== false,
      category: imageData.category || '',
      tags: imageData.tags || [],
    }));

    return images
      .filter(img => img.visible !== false)
      .sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return b.uploadedAt - a.uploadedAt;
      });
  }

  /**
   * Load gallery from local JSON file as fallback
   */
  private async loadLocalGallery(): Promise<GalleryImage[]> {
    try {
      const response = await fetch('/assets/gallery/gallery.json');
      if (!response.ok) {
        console.log('[GalleryService] Local gallery.json not found');
        return [];
      }

      const images: GalleryImage[] = await response.json();
      
      const visibleImages = images.filter(img => img.visible !== false);
      visibleImages.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return b.uploadedAt - a.uploadedAt;
      });

      console.log('[GalleryService] Loaded local gallery:', visibleImages.length, 'images');
      return visibleImages;
    } catch (error) {
      console.error('[GalleryService] Error loading local gallery:', error);
      return [];
    }
  }

  /**
   * Get gallery images once (no real-time updates).
   * Uses a timeout so a stuck Firebase `get` cannot leave the UI in loading state forever.
   */
  async getGalleryImages(): Promise<GalleryImage[]> {
    const FETCH_TIMEOUT_MS = 15000;

    const inner = async (): Promise<GalleryImage[]> => {
      console.log('[GalleryService] Getting gallery images (one-time fetch)');

      if (this.useFirebase) {
        try {
          const galleryRef = this.getGalleryRef();
          if (!galleryRef) {
            return this.loadLocalGallery();
          }
          const snapshot = await get(galleryRef);
          const data = snapshot.val();

          if (!data) {
            return this.loadLocalGallery();
          }

          return this.parseFirebaseData(data);
        } catch (error) {
          console.error('[GalleryService] Firebase fetch error:', error);
          return this.loadLocalGallery();
        }
      }

      return this.loadLocalGallery();
    };

    return Promise.race([
      inner(),
      new Promise<GalleryImage[]>((_, reject) => {
        setTimeout(() => reject(new Error('Gallery fetch timed out')), FETCH_TIMEOUT_MS);
      }),
    ]).catch((err) => {
      console.warn('[GalleryService] getGalleryImages falling back to local after error/timeout:', err);
      return this.loadLocalGallery();
    });
  }

  /**
   * Get gallery images by category
   */
  async getGalleryImagesByCategory(category: string): Promise<GalleryImage[]> {
    const allImages = await this.getGalleryImages();
    return allImages.filter((img) => img.category?.toLowerCase() === category.toLowerCase());
  }

  /**
   * Extract unique categories from gallery images
   */
  async getGalleryCategories(): Promise<string[]> {
    const allImages = await this.getGalleryImages();
    const categories = allImages
      .map((img) => img.category)
      .filter((category): category is string => category !== undefined && category.trim() !== '');

    const uniqueCategories = Array.from(new Set(categories));
    uniqueCategories.sort();

    return ['All', ...uniqueCategories];
  }
}

export const galleryService = new GalleryService();
